import { Formik, Form, useFormikContext } from "formik";
import * as React from "react";

import { FormModel } from "../types/FormTypes";
import { useAtom } from "jotai";
import * as Yup from "yup";
import { formDataAtom, travelSearchAtom, selectedJourneyAtom } from "../App";
import { useNavigate } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import { useDebounce } from "use-debounce";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import updateLocale from "dayjs/plugin/updateLocale";
import "dayjs/locale/sv";
import useFocusOnLoad from "../hooks/useFocusOnLoad";
import ScreenReaderAnnouncer, {
  getJourneySrLabel,
} from "../components/ScreenReaderAnnouncer";

import {
  Box,
  Button,
  handleSubmitClick,
  ErrorList,
  Progressbar,
  DatePicker,
  CustomAutocomplete,
  Input,
  Label,
  Alert,
  AlertTitle,
  RadioGroupForm,
  cn,
  TrafficBadge,
  Heading,
} from "@ogt/ui";

interface Stop {
  PlaceName: string;
  OgtType: string;
  Ll: [number, number];
  Id: number;
}
interface Ogt {
  OgtTrafficTypeGroupCssClass: string;
  OgtTrafficTypeIconCssClass: string;
  OgtTrafficTypeIconReplaceLineName: boolean;
  OgtLineNamePreformatted: string;
}

interface Line {
  LineName: string;
  LineTypeId: number;
  LineTypeName: string;
  IsWalk: boolean;
  Ogt: Ogt;
  TrainNo?: number;
}
interface RouteLink {
  Line: Line;
}
interface Journeys {
  Arrival: string;
  Departure: string;
  JourneyKey: string;
  TravelTime: string;
  Switch: string;
  RouteLinks: RouteLink[] | null;
}

type TrafficMeta = Parameters<typeof TrafficBadge>[0]["meta"];

function toTrafficMeta(link: RouteLink): TrafficMeta {
  return {
    iconCss: link.Line.Ogt.OgtTrafficTypeIconCssClass,
    groupCss: link.Line.Ogt.OgtTrafficTypeGroupCssClass,
    title: link.Line.Ogt.OgtLineNamePreformatted ?? link.Line.LineName,
  };
}

function JourneySelectionWatcher({
  searchResults,
  onSelect,
}: {
  searchResults: Journeys[];
  onSelect: (value: string, result: Journeys) => void;
}) {
  const { values } = useFormikContext<any>();

  React.useEffect(() => {
    const key: string | undefined = values?.journey;
    if (!key) return;
    const sel = searchResults.find((j) => j.JourneyKey === key);
    if (sel) onSelect(key, sel);
  }, [values?.journey, searchResults, onSelect]);

  return null;
}

export default function TravelSearch() {
  const apiBaseUrl =
    process.env.REACT_APP_TPS_API_BASE_URL?.replace(/\/$/, "") ?? "";

  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const progress = 10;

  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);

  const [formData, setFormData] = useAtom(formDataAtom);
  const [travelSearch, setTravelSearch] = useAtom(travelSearchAtom);
  const [selectedJourney, setSelectedJourney] = useAtom(selectedJourneyAtom);
  const [autocompleteAnnouncement, setAutocompleteAnnouncement] = useState("");
  const [searchErrors, setSearchErrors] = useState({
    journeyFromError: "",
    journeyToError: "",
    journeyTimeError: "",
    journeyDateError: "",
    journeyFormError: "",
    formError: "",
  });

  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const [fromQuery, setFromQuery] = useState<string>("");
  const [toQuery, setToQuery] = useState<string>("");
  const [fromResults, setFromResults] = useState<Stop[]>([]);
  const [toResults, setToResults] = useState<Stop[]>([]);
  const [searchResults, setSearchResults] = useState<Journeys[]>([]);

  const [searchType, setSearchType] = useState<"from" | "to" | null>(null);

  const [debouncedFromQuery] = useDebounce(fromQuery, 300);
  const [debouncedToQuery] = useDebounce(toQuery, 300);

  dayjs.extend(localeData);
  dayjs.extend(updateLocale);

  dayjs.updateLocale("sv", {
    weekdays: [
      "Söndag",
      "Måndag",
      "Tisdag",
      "Onsdag",
      "Torsdag",
      "Fredag",
      "Lördag",
    ],
    months: [
      "januari",
      "februari",
      "mars",
      "april",
      "maj",
      "juni",
      "juli",
      "augusti",
      "september",
      "oktober",
      "november",
      "december",
    ],
  });

  useEffect(() => {
    if (!searchType) return;

    const results = searchType === "from" ? fromResults : toResults;

    if (!results) return;

    if (results.length > 0) {
      setAutocompleteAnnouncement(
        `Det finns ${results.length} sökförslag. Använd upp och nedpilar för att navigera.`
      );
    } else {
      setAutocompleteAnnouncement("");
    }

  }, [fromResults, toResults, searchType]);

  useEffect(() => {
    if (
      travelSearch.selectedJourneyFrom &&
      travelSearch.selectedJourneyTo &&
      travelSearch.journeyDate &&
      travelSearch.journeyTime
    ) {
      doTravelSearch(false, false);

      setFromQuery(travelSearch.selectedJourneyFrom.PlaceName);
      setToQuery(travelSearch.selectedJourneyTo.PlaceName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchType) return;

    const query = searchType === "from" ? debouncedFromQuery : debouncedToQuery;
    const trimmed = (query ?? "").trim();
    if (trimmed.length < 2) {
      if (searchType === "from") setFromResults([]);
      else setToResults([]);
      return;
    }

    const params = new URLSearchParams();
    params.set("q", trimmed); // only q

    fetch(`${apiBaseUrl}/api/v1/Points/Find?${params.toString()}`)
      .then(async (res) => {
        const text = await res.text();
        if (!res.ok) {
          console.warn("Points/Find bad response:", res.status, text);
          if (searchType === "from") setFromResults([]);
          else setToResults([]);
          return null;
        }
        return JSON.parse(text);
      })
      .then((data) => {
        if (!data) return;
        const arr: Stop[] = Array.isArray(data) ? data : [];
        if (searchType === "from") setFromResults(arr);
        else setToResults(arr);
      })
      .catch((err) => {
        console.error("Points/Find fetch error:", err);
        if (searchType === "from") setFromResults([]);
        else setToResults([]);
      });
  }, [debouncedFromQuery, debouncedToQuery, searchType, apiBaseUrl]);

  const searchFormRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const handleFocus = () => {
      if (searchFormRef.current) {
        const isActive = searchFormRef.current.contains(document.activeElement);
        setIsFocused(isActive);
      }
    };
    document.addEventListener("focusin", handleFocus);
    document.addEventListener("focusout", handleFocus);

    return () => {
      document.removeEventListener("focusin", handleFocus);
      document.removeEventListener("focusout", handleFocus);
    };
  }, []);

  const handleSelect = (placeName: string, type: "from" | "to") => {
    const list = type === "from" ? fromResults : toResults;
    const safeList: Stop[] = Array.isArray(list) ? list : [];
    const selectedItem =
      safeList.find((item) => item.PlaceName === placeName) || null;

    if (type === "from") {
      setTravelSearch((prev) => ({
        ...prev,
        selectedJourneyFrom: selectedItem,
      }));
      setFromQuery(selectedItem?.PlaceName ?? "");
      setFromResults([]);
    } else {
      setTravelSearch((prev) => ({ ...prev, selectedJourneyTo: selectedItem }));
      setToQuery(selectedItem?.PlaceName ?? "");
      setToResults([]);
    }
  };

  const handleQueryChange = (value: string, type: "from" | "to") => {
    if (type === "from") {
      setFromQuery(value);
      if (value.trim() === "") {
        setTravelSearch((p) => ({ ...p, selectedJourneyFrom: null }));
        setFromResults([]);
      }
    } else {
      setToQuery(value);
      if (value.trim() === "") {
        setTravelSearch((p) => ({ ...p, selectedJourneyTo: null }));
        setToResults([]);
      }
    }
    setSearchType(type);
  };

  const handleTravelSearchChange = (
    field: keyof typeof travelSearch,
    value: string
  ) => {
    setTravelSearch((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      doTravelSearch(true);
    }
  };

  const formatTimeInput = (value: string) => {
    const cleanedInput = value.replace(/\D/g, "");
    let newTime = "00:00";

    if (cleanedInput.length === 1) {
      newTime = `0${cleanedInput}:00`;
    } else if (cleanedInput.length === 2) {
      const hours = cleanedInput;
      newTime = `${hours === "24" ? "00" : hours}:00`;
    } else if (cleanedInput.length === 3) {
      const hours =
        cleanedInput[0] === "2" && cleanedInput[1] === "4"
          ? "00"
          : `0${cleanedInput[0]}`;
      const minutes = cleanedInput.slice(1);
      newTime = `${hours}:${minutes}`;
    } else if (cleanedInput.length >= 4) {
      let hours = cleanedInput.slice(0, 2);
      const minutes = cleanedInput.slice(2, 4);
      if (hours === "24") {
        hours = "00";
      }
      newTime = `${hours}:${minutes}`;
    }

    setTravelSearch((prev) => ({
      ...prev,
      journeyTime: newTime,
    }));

    return newTime;
  };
  const doTravelSearch = (
    clearSelectedJourney: boolean = false,
    announce: boolean = true
  ) => {
    if (announce) {
      setHasSearched(true);
      setIsSearching(true);
    }

    const errors = {
      journeyFromError: "",
      journeyToError: "",
      journeyDateError: "",
      journeyTimeError: "",
    };

    if (
      !travelSearch.selectedJourneyFrom?.PlaceName.length ||
      travelSearch.selectedJourneyFrom.PlaceName.length < 1
    ) {
      errors.journeyFromError = "Vänligen fyll i en avreseplats";
    }

    if (
      !travelSearch.selectedJourneyTo?.PlaceName.length ||
      travelSearch.selectedJourneyTo.PlaceName.length < 1
    ) {
      errors.journeyToError = "Vänligen fyll i en destination";
    }

    if (travelSearch.journeyDate.length < 1) {
      errors.journeyDateError = "Vänligen fyll i ett datum";
    }

    if (travelSearch.journeyTime.length < 1) {
      errors.journeyTimeError = "Vänligen fyll i en tid";
    }

    const today = dayjs().startOf("day");
    const selectedDate = dayjs(travelSearch.journeyDate);

    if (selectedDate.isAfter(today)) {
      errors.journeyDateError =
        "Vänligen ange ett datum som är idag eller tidigare";
    }

    setSearchErrors((prevErrors) => ({
      ...prevErrors,
      ...errors,
    }));

    const hasErrors = Object.values(errors).some(
      (error) => error.trim().length > 0
    );

    if (hasErrors) {
      setIsSearching(false);
      setSearchResults([]);
      setFormData((prev) => ({
        ...prev,
        journey: "",
      }));
      return;
    }

    if (clearSelectedJourney) {
      setSelectedJourney({
        fromName: "",
        toName: "",
        date: "",
        travelTime: "",
        journeyKey: "",
        line: "",
        switches: "",
        unformattedDate: "",
      });

      setFormData((prev) => ({
        ...prev,
        journey: "",
        //selectedJourney,
      }));
    }

    const params = {
      startId: travelSearch.selectedJourneyFrom?.Id,
      endId: travelSearch.selectedJourneyTo?.Id,
      startType: travelSearch.selectedJourneyFrom?.OgtType,
      endType: travelSearch.selectedJourneyTo?.OgtType,
      startLl: `${travelSearch.selectedJourneyFrom?.Ll[0]},${travelSearch.selectedJourneyFrom?.Ll[1]}`,
      endLl: `${travelSearch.selectedJourneyTo?.Ll[0]},${travelSearch.selectedJourneyTo?.Ll[1]}`,
      startName: travelSearch.selectedJourneyFrom?.PlaceName,
      endName: travelSearch.selectedJourneyTo?.PlaceName,
      date: travelSearch.journeyDate,
      time: formatTimeInput(travelSearch.journeyTime),
      direction: 0,
      span: "Default",
      trafficTypes: 0,
      changetime: "Normal",
      priority: "ShortestTime",
      walk: true,
    };

    const searchParams = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).map(([key, value]) => [key, String(value)])
      )
    );

    fetch(`${apiBaseUrl}/api/v1/Journeys/Find?${searchParams.toString()}`)
      .then((response) => response.json())
      .then((data) => {
        let errorText = "";
        setSearchResults(data.Journeys || []);
        if (data.ErrorText && data.ErrorText.includes("JourneyDateTime")) {
          errorText = "Vänligen ange en giltig tid";
        } else {
          errorText = data.ErrorText;
        }
        setSearchErrors({
          journeyFromError: "",
          journeyToError: "",
          journeyTimeError: "",
          journeyDateError: "",
          journeyFormError: errorText || "",
          formError: "",
        });
        setIsSearching(false);
      })
      .catch((error) => {
        console.error("Error:", error);
        setIsSearching(false);
      });
  };

  const formatSelectedJourney = (journey: any) => {
    const lineNames = (journey.RouteLinks || [])
      .map((routeLink: any) => {
        const lineName = routeLink.Line.LineName;
        const trainNo = routeLink.Line.TrainNo;
        return trainNo !== 0 ? `${lineName} (#${trainNo})` : lineName;
      })
      .join(", ");

    const formattedJourney = {
      fromName: journey.UsedStartPlace.PlaceName,
      toName: journey.UsedEndPlace.PlaceName,
      date: formatDate(journey.Departure, "dddd D MMMM"),
      travelTime: `${formatDate(journey.Departure, "HH:mm")} - ${formatDate(
        journey.Arrival,
        "HH:mm"
      )}`,
      journeyKey: journey.JourneyKey,
      line: lineNames,
      switches: journey.Switch,
      unformattedDate: journey.Departure,
    };
    return formattedJourney;
  };

  const formatDate = (date: string, format: string) => {
    const formattedDate = dayjs(date).locale("sv").format(format);
    return formattedDate;
  };

  const groupResultsByDate = (results: Journeys[]) => {
    return results.reduce((acc: Record<string, Journeys[]>, result) => {
      const date = formatDate(result.Departure, "dddd D MMMM");
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(result);
      return acc;
    }, {});
  };

  const updateFormData = (value: string, result: any) => {
    setSelectedJourney(formatSelectedJourney(result));
  };

  return (
    <div className="travel-search-wrapper">
      <Formik<FormModel>
        onSubmit={(values) => {
          setFormData({
            ...formData,
            ...values,
            selectedJourney,
          });
          navigate("/hur-sen-blev-du");
        }}
        initialValues={{
          ...formData,
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          journey: Yup.string().test(
            "at-least-one-radio-selected",
            "Vänligen välj en resa för att gå vidare till nästa steg",
            (value) => {
              return searchResults.some(
                (result) => result.JourneyKey === value
              );
            }
          ),
        })}
      >
        {({ errors, values }) => {
          const politeAnnouncement = !hasSearched
            ? ""
            : isSearching
              ? "Söker resor."
              : searchErrors.journeyFormError
                ? ""
                : searchResults.length === 0
                  ? "Inga resor hittades."
                  : `${searchResults.length} ${searchResults.length === 1 ? "resa hittades" : "resor hittades"
                  }. Välj en resa i listan nedan.`;

          const assertiveAnnouncement =
            searchErrors.journeyFormError ||
            searchErrors.journeyFromError ||
            searchErrors.journeyToError ||
            searchErrors.journeyDateError ||
            searchErrors.journeyTimeError ||
            (errors?.journey ? String(errors.journey) : "");

          const combinedPoliteMessage = [politeAnnouncement, autocompleteAnnouncement]
            .filter(Boolean)
            .join(" ");

          return (
            <>
              <ScreenReaderAnnouncer
                politeMessage={combinedPoliteMessage}
                assertiveMessage={assertiveAnnouncement}
              />

              <div className="flex justify-end items-center">
                <div className="w-full max-w-xs mb-4">
                  <Progressbar percentage={progress} />
                </div>
              </div>

              <Form ref={formRef} noValidate>
                <JourneySelectionWatcher
                  searchResults={searchResults}
                  onSelect={(value, result) => updateFormData(value, result)}
                />

                <Box fullwidth={true}>
                  <Heading
                    progress={progress}
                    headingRef={h2Ref}
                    focusable
                    id="TravelSearchHeading"
                  >
                    Hur såg din planerade resa ut?
                  </Heading>

                  <p className="mb-6">
                    Vi börjar med vilken avgång det gäller. Var åkte du ifrån,
                    vart skulle du och när var det? Fyll i uppgifterna här för
                    att få förslag på resor.
                  </p>

                  {searchErrors.journeyFormError.length > 0 && (
                    <Alert variant="error" showIcon>
                      <AlertTitle>{searchErrors.journeyFormError}</AlertTitle>
                    </Alert>
                  )}

                  <div
                    className="travel-search-form grid gap-6 grid-cols-1 md:grid-cols-2"
                    ref={searchFormRef}
                  >
                    <CustomAutocomplete
                      id="journeyFrom"
                      label="Från"
                      errorMessage={searchErrors.journeyFromError}
                      value={
                        fromQuery !== undefined
                          ? fromQuery
                          : travelSearch.selectedJourneyFrom?.PlaceName || ""
                      }
                      onChange={(e: any) => {
                        setFromQuery(e.target.value);
                        setSearchType("from");
                      }}
                      onQueryChange={handleQueryChange}
                      options={fromResults}
                      onSelect={(placeName: any) =>
                        handleSelect(placeName, "from")
                      }
                      type="from"
                      onKeyDown={handleKeyDown}
                    />

                    <CustomAutocomplete
                      id="journeyTo"
                      label="Till"
                      errorMessage={searchErrors.journeyToError}
                      value={
                        toQuery !== undefined
                          ? toQuery
                          : travelSearch.selectedJourneyTo?.PlaceName || ""
                      }
                      onChange={(e: any) => {
                        setToQuery(e.target.value);
                        setSearchType("to");
                      }}
                      onQueryChange={handleQueryChange}
                      options={toResults}
                      onSelect={(placeName: any) =>
                        handleSelect(placeName, "to")
                      }
                      type="to"
                      onKeyDown={handleKeyDown}
                    />

                    <DatePicker
                      name="journey_date"
                      label={
                        <>
                          Datum för resa <span>(Obligatoriskt)</span>
                        </>
                      }
                      value={travelSearch.journeyDate}
                      onChange={(value) =>
                        handleTravelSearchChange("journeyDate", value)
                      }
                      error={searchErrors.journeyDateError}
                      maxDate={new Date().toISOString().split("T")[0]}
                      onKeyDown={handleKeyDown}
                      disableFutureDates
                    />

                    <div className="input-container gap-2 flex flex-col">
                      <Label htmlFor="journey_time">
                        <span>
                          Ungefärlig avgångstid <span>(Obligatoriskt)</span>
                        </span>
                      </Label>

                      <Input
                        type="text"
                        id="journey_time"
                        name="journey_time"
                        value={travelSearch.journeyTime || ""}
                        onChange={(e) =>
                          handleTravelSearchChange(
                            "journeyTime",
                            e.target.value
                          )
                        }
                        onKeyDown={handleKeyDown}
                        className={`${searchErrors.journeyTimeError ? "has-error" : ""
                          }`}
                      />

                      {searchErrors.journeyTimeError.length > 0 && (
                        <Alert
                          showIcon
                          onDismiss={() => { }}
                          onOpenChange={() => { }}
                          variant="error"
                        >
                          <AlertTitle>
                            {searchErrors.journeyTimeError}
                          </AlertTitle>
                        </Alert>
                      )}
                    </div>
                  </div>

                  <Button
                    onClick={() => doTravelSearch(true)}
                    className="mt-6 w-full sm:w-auto"
                    variant="primary"
                    type="button"
                    id="searchButton"
                    ref={searchButtonRef}
                  >
                    Sök resa
                  </Button>

                  {Object.keys(errors).length > 0 && (
                    <div className="mt-4">
                      <ErrorList errors={errors} />
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <div className="search-results-container mt-6 flex flex-col gap-6">
                      {Object.entries(groupResultsByDate(searchResults)).map(
                        ([date, results]) => (
                          <div key={date}>
                            {errors.journey && (
                              <span className="validation-error">
                                {errors.journey.toString()}
                              </span>
                            )}

                            {(() => {
                              const uniqueResults = results.filter(
                                (item, index, self) =>
                                  index ===
                                  self.findIndex(
                                    (t) => t.JourneyKey === item.JourneyKey
                                  )
                              );

                              const options = uniqueResults.map((result) => ({
                                id: result.JourneyKey,
                                value: result.JourneyKey,
                                label: (
                                  <>
                                    <span className="sr-only">
                                      {getJourneySrLabel(result, formatDate)}
                                    </span>

                                    <div
                                      aria-hidden="true"
                                      className={cn(
                                        "flex flex-row w-full gap-x-6 gap-y-2 items-center *:leading-normal flex-wrap",
                                        values.journey === result.JourneyKey
                                          ? "checked"
                                          : ""
                                      )}
                                    >
                                      <span className="lineicon-container flex-wrap gap-1 inline-flex items-center">
                                        {Array.isArray(result.RouteLinks) &&
                                          result.RouteLinks.map(
                                            (item, index) => {
                                              const meta = toTrafficMeta(item);
                                              const isPendeln =
                                                item.Line.LineName?.includes(
                                                  "Östgötapendeln"
                                                );
                                              const showText =
                                                !item.Line.Ogt
                                                  .OgtTrafficTypeIconReplaceLineName;

                                              return (
                                                <TrafficBadge
                                                  key={index}
                                                  meta={meta}
                                                  className={cn(
                                                    "lineicon",
                                                    item.Line.LineTypeName,
                                                    item.Line.Ogt
                                                      .OgtTrafficTypeGroupCssClass,
                                                    isPendeln && "pendeln"
                                                  )}
                                                  titleOverride={undefined}
                                                >
                                                  {showText
                                                    ? item.Line.LineName
                                                    : null}
                                                </TrafficBadge>
                                              );
                                            }
                                          )}
                                      </span>

                                      <span className="group-hover/radio-row:underline travel-departure-arrival inline-flex items-center">
                                        {formatDate(result.Departure, "HH:mm")}{" "}
                                        - {formatDate(result.Arrival, "HH:mm")}
                                      </span>

                                      <span className="travel-time inline-flex items-center md:ml-auto min-w-[100px]">
                                        Restid: {result.TravelTime}
                                      </span>

                                      <span className="switch inline-flex items-center min-w-[60px]">
                                        {result.Switch}{" "}
                                        {parseInt(result.Switch as any, 10) ===
                                          1
                                          ? "byte"
                                          : "byten"}
                                      </span>
                                    </div>
                                  </>
                                ),
                              }));

                              return (
                                <>
                                  <Heading as="h3">
                                    {date}, {uniqueResults.length}{" "}
                                    {uniqueResults.length > 1
                                      ? "resor"
                                      : "resa"}
                                  </Heading>

                                  <RadioGroupForm
                                    name="journey"
                                    legend="Välj en resa från listan"
                                    orientation="vertical"
                                    variant="box"
                                    required
                                    underlineOnHover={false}
                                    className={`radio-group travel-search-results w-full! ${errors.journey ? " has-error" : ""
                                      }`}
                                    itemClassName=""
                                    options={options}
                                  />
                                </>
                              );
                            })()}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </Box>

                <div className="flex justify-center">
                  <div className="w-full text-left flex items-center">
                    <Button
                      iconPosition="left"
                      iconSize={16}
                      iconName="arrow-left"
                      aria-label="Tillbaka"
                      showIcon
                      size="medium"
                      variant="outline"
                      onClick={(event) => {
                        event.preventDefault();
                        const backUrl = undefined;
                        if (backUrl) {
                          navigate(backUrl, { replace: true });
                        } else {
                          navigate(-1);
                        }
                      }}
                    >
                      Tillbaka
                    </Button>
                  </div>

                  <div className="w-full text-right">
                    {searchResults.length > 0 && (
                      <Button
                        onClick={handleSubmitClick(formRef)}
                        className="btn primary"
                        disabled={isFocused}
                        type="submit"
                      >
                        Gå vidare
                      </Button>
                    )}
                  </div>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
}
